import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import useDelete from '@/core/composables/api/useDelete'
import usePut from '@/core/composables/api/usePut'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import { useSnackStore } from '@/stores/snack.store'

interface BookmarkableImpulse {
  id: string
  bookmarkedAt: string | null
}

export const useBookmarkLessonImpulse = (impulse: MaybeRefOrGetter<BookmarkableImpulse>) => {
  const { t } = useI18n()
  const snackStore = useSnackStore()
  const endpoint = computed(() => `lesson-impulse/${toValue(impulse).id}/bookmark`)
  const { mutate, status: bookmarkStatus } = usePut<void>(endpoint)
  const { remove, status: unbookmarkStatus } = useDelete<void>(endpoint)

  const pending = computed(
    () =>
      bookmarkStatus.value === ApiRequestStatus.Loading ||
      unbookmarkStatus.value === ApiRequestStatus.Loading,
  )

  const applyBookmark = (id: string, bookmarkedAt: string | null) => {
    const current = toValue(impulse)
    if (current.id !== id) {
      return
    }
    if (bookmarkedAt && current.bookmarkedAt) {
      return
    }
    Object.assign(current, { bookmarkedAt })
  }

  const bookmark = async () => {
    const current = toValue(impulse)
    if (current.bookmarkedAt) {
      return
    }

    const id = current.id
    await mutate(undefined)

    if (bookmarkStatus.value === ApiRequestStatus.Success) {
      applyBookmark(id, new Date().toISOString())
      snackStore.showInfo(t('lumia.bookmark.added'), {
        label: t('lumia.bookmark.undo'),
        callback: () => {
          void unbookmark()
        },
      })
      return
    }

    snackStore.showError(t('lumia.bookmark.error'))
  }

  const unbookmark = async () => {
    const id = toValue(impulse).id
    await remove()

    if (unbookmarkStatus.value === ApiRequestStatus.Success) {
      applyBookmark(id, null)
      snackStore.showInfo(t('lumia.bookmark.removed'), {
        label: t('lumia.bookmark.undo'),
        callback: () => {
          void bookmark()
        },
      })
      return
    }

    snackStore.showError(t('lumia.bookmark.error'))
  }

  const toggleBookmark = async () => {
    if (pending.value) {
      return
    }
    if (toValue(impulse).bookmarkedAt) {
      await unbookmark()
      return
    }
    await bookmark()
  }

  return { toggleBookmark, pending }
}
